import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-solid-svg-icons/faCalendar";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { esES } from "@mui/x-date-pickers/locales";
import { isValid } from "date-fns";
import { enUS, es } from "date-fns/locale";
import { CreateAccountDateOfBirthField } from "./CreateAccountForm.styles";
import { normalizeLanguage } from "../../i18n/languagePreference";
import { MIN_DATE_OF_BIRTH } from "./types";

const SolidCalendarIcon = () => <FontAwesomeIcon icon={faCalendar} />;

export const DateOfBirthInput: React.FC<{
  value: Date | null;
  onChange: (date: Date | null) => void;
  onFocus?: () => void;
  error?: string;
  label?: string;
}> = ({ value: date, onChange, onFocus, error, label }) => {
  const { t, i18n } = useTranslation();
  const spanish = normalizeLanguage(i18n.language) === "es";
  const previousSpanish = useRef(spanish);

  useEffect(() => {
    if (previousSpanish.current === spanish) return;
    previousSpanish.current = spanish;
    // The same text may mean a different date in the new format. Require a
    // fresh entry when the previous one was incomplete or invalid.
    if (date && !isValid(date)) onChange(null);
  }, [spanish, date, onChange]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={spanish ? es : enUS}
      localeText={
        spanish
          ? esES.components.MuiLocalizationProvider.defaultProps.localeText
          : undefined
      }
    >
      {/* Keep keyboard entry on touch devices. */}
      <DesktopDatePicker
        views={["year", "month", "day"]}
        openTo="year"
        value={date}
        inputFormat={spanish ? "dd-MM-yyyy" : "MM-dd-yyyy"}
        minDate={MIN_DATE_OF_BIRTH}
        disableFuture
        components={{ OpenPickerIcon: SolidCalendarIcon }}
        onChange={(next, keyboardInput) => {
          // Year/month selections are drafts until a day is accepted.
          // Keyboard entry still updates immediately.
          if (keyboardInput === undefined) return;
          const incomplete =
            keyboardInput && keyboardInput.replace(/\D/g, "").length < 8;
          const parsedDate = incomplete ? new Date(NaN) : next;
          onChange(parsedDate);
        }}
        onAccept={onChange}
        OpenPickerButtonProps={{
          "aria-label": t("createAccount.fields.chooseDate"),
          sx: { minWidth: 44, minHeight: 44, mr: -1.5 },
        }}
        renderInput={(params) => (
          <CreateAccountDateOfBirthField
            {...params}
            id="create-account-dob"
            name="dob"
            label={label ?? t("createAccount.fields.dateOfBirth")}
            onFocus={onFocus}
            error={error}
            FormHelperTextProps={{ role: "alert" }}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: true,
              required: true,
            }}
            inputProps={{
              ...params.inputProps,
              onChange: (event) => {
                if (!event.currentTarget.value) {
                  onChange(null);
                }
                params.inputProps?.onChange?.(event);
              },
              "aria-required": true,
              autoComplete: "bday",
              inputMode: "numeric",
              placeholder: t("createAccount.fields.dateOfBirthFormat"),
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
};
